import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import PropTypes from 'prop-types';

import {
  Wrapper,
  SlideContainer,
  CounterWrapper,
  StoryCounter,
  StoryContent,
  StyledImage,
  StyledVideo,
  YearWrapper,
  Year,
  Month,
  Description
} from './styled';

const Slide = ({
  slide,
  slideIndex,
  marginRight,
  isDragging,
  isActiveSlide,
  activeSlideIndex,
  isLastSlide,
  isFirstSlide,
  goToSlide,
  inView,
  slides
}) => {
  const { description, isColumn, stories, transform, width, year } = slide;
  const [activeStoryIndex, setActiveStoryIndex] = useState(0);

  const activeStory = stories[activeStoryIndex];
  const isFirstStory = activeStoryIndex === 0;
  const isLastStory = activeStoryIndex === stories.length - 1;

  const slideControls = useAnimation();
  const counterControls = useAnimation();

  const slideRef = useRef(null);
  const videoRef = useRef(null);

  const counterVariants = {
    visible: (duration) => ({
      width: '100%',
      transition: {
        duration: duration / 1000,
        ease: 'linear'
      },
      transitionEnd: { width: '100%' }
    }),

    visibleFast: {
      width: '100%',
      transition: {
        duration: 0.1,
        ease: 'linear'
      }
    },

    hidden: {
      width: 0
    }
  };

  const slideVariants = {
    straight: {
      rotate: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        duration: 1,
        delay: 0.2
      }
    },

    rotate: ({ rotate }) => ({
      rotate,
      opacity: 0.5,
      transition: {
        duration: 0.5
      }
    })
  };

  const handleNext = () => {
    if (!isLastStory) {
      setActiveStoryIndex(activeStoryIndex + 1);
    }

    if (isLastStory && !isLastSlide) {
      goToSlide(activeSlideIndex + 1);
      setActiveStoryIndex(0);
    }

    if (isLastStory && isLastSlide) {
      goToSlide(0);
      setActiveStoryIndex(0);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStory) {
      setActiveStoryIndex(activeStoryIndex - 1);
    }

    if (isFirstStory && !isFirstSlide) {
      goToSlide(activeSlideIndex - 1);
      setActiveStoryIndex(0);
    }
  };

  const handleActiveSlideClick = (e) => {
    const slideWidth = slideRef.current.getBoundingClientRect().width;
    const halfSlideWidth = slideWidth / 2;
    const mouseXPos = e.nativeEvent.offsetX;
    const isClickOnLeftHalf = mouseXPos <= halfSlideWidth;

    if (isClickOnLeftHalf) {
      handlePrevious();
    } else {
      handleNext();
    }
  };

  const handleSlideClick = (e) => {
    if (isActiveSlide && !isDragging) {
      handleActiveSlideClick(e);
    } else if (!isActiveSlide && !isDragging) {
      goToSlide(slideIndex);
      setActiveStoryIndex(0);
    }
  };

  const renderStoryCounter = () => {
    return stories.map((story, storyIndex) => {
      return (
        <StoryCounter
          key={story.id}
          storyIsViewed={isActiveSlide && activeStoryIndex > storyIndex}
        >
          {isActiveSlide && activeStoryIndex === storyIndex && (
            <motion.div
              custom={story.duration}
              variants={counterVariants}
              animate={counterControls}
              initial={{ width: 0 }}
            />
          )}
        </StoryCounter>
      );
    });
  };

  const renderStoryContent = () => {
    if (isColumn) {
      return (
        <StoryContent>
          <StyledImage src={slide.stories[activeStoryIndex].content1} isColumn />
          <StyledImage src={slide.stories[activeStoryIndex].content2} isColumn />
        </StoryContent>
      );
    }

    return (
      <StoryContent>
        {stories.map((story, storyIndex) => {
          if (story?.type === 'video') {
            return (
              <StyledVideo
                key={story?.id}
                ref={videoRef}
                src={story.content}
                type="video/mp4"
                isActiveStory={activeStoryIndex === storyIndex}
                autoPlay
                playsInline
              />
            );
          }

          return (
            <StyledImage
              key={story?.id}
              src={story.content}
              isActiveStory={activeStoryIndex === storyIndex}
            />
          );
        })}
      </StoryContent>
    );
  };

  useEffect(() => {
    if (isActiveSlide) {
      slideControls.start('straight');
    } else {
      slideControls.start('rotate');
      setActiveStoryIndex(0);
    }
  }, [isActiveSlide]);

  useEffect(() => {
    if (isActiveSlide && inView) {
      const storyTimer = setTimeout(() => {
        handleNext();
      }, activeStory?.duration);

      counterControls.start('visible');
      return () => clearTimeout(storyTimer);
    }
  }, [isActiveSlide, activeStory, inView]);

  useEffect(() => {
    if (!inView) {
      setActiveStoryIndex(0);
      goToSlide(2);
      counterControls.start('hidden');
    }
  }, [inView]);

  useEffect(() => {
    const video = videoRef?.current;

    if (isActiveSlide && video && stories[activeStoryIndex]?.type === 'video' && inView) {
      video.play();
    } else if (
      (stories[activeStoryIndex]?.type !== 'video' && video) ||
      (!inView && video)
    ) {
      video.pause();
      video.currentTime = 0;
    }
  }, [videoRef, activeStoryIndex, isActiveSlide, inView]);

  useEffect(() => {
    const video = videoRef?.current;

    if (video) {
      video.muted = true;
    }
  }, [videoRef]);

  useEffect(() => {
    const video = videoRef?.current;

    if (video) {
      const activeSlideStories = slides[activeSlideIndex]?.stories;

      const slideContainsVideo = activeSlideStories.some(
        (story) => story?.type === 'video'
      );

      if (!slideContainsVideo) {
        video.pause();
        video.currentTime = 0;
      }
    }
  }, [activeSlideIndex]);

  return (
    <Wrapper width={width} marginRight={marginRight}>
      <YearWrapper
        marginRight={marginRight}
        isFirstSlide={isFirstSlide}
        isLastSlide={isLastSlide}
        isActiveSlide={isActiveSlide}
      >
        <Year isActiveSlide={isActiveSlide}>{year}</Year>
      </YearWrapper>

      <Month isActiveSlide={isActiveSlide}>{activeStory?.month}</Month>

      <SlideContainer
        isColumn={isColumn}
        onClick={handleSlideClick}
        animate={slideControls}
        variants={slideVariants}
        custom={transform}
        initial="rotate"
        ref={slideRef}
      >
        <CounterWrapper>{isActiveSlide && renderStoryCounter()}</CounterWrapper>
        {renderStoryContent()}
      </SlideContainer>

      <Description isActiveSlide={isActiveSlide}>{description}</Description>
    </Wrapper>
  );
};

export default Slide;

Slide.propTypes = {
  slide: PropTypes.object.isRequired,
  isActiveSlide: PropTypes.bool.isRequired,
  goToSlide: PropTypes.func.isRequired,
  isDragging: PropTypes.bool.isRequired,
  activeSlideIndex: PropTypes.number.isRequired,
  slideIndex: PropTypes.number.isRequired,
  isLastSlide: PropTypes.bool.isRequired,
  marginRight: PropTypes.number.isRequired,
  isFirstSlide: PropTypes.bool.isRequired,
  inView: PropTypes.bool.isRequired,
  slides: PropTypes.array.isRequired
};
